import React, { useContext, useMemo, useState } from 'react'
import { orderBy } from 'lodash'
import {
  ColorMarker,
  Charts,
  PrettyNumber,
  IGridTableColumn,
  NewGridTable,
  GridTableStatusEnum,
} from '@ui'
import { PrintTitle, TabContext } from '../Details'
import { IPortfolioSpaceUsageTypes, Order } from '@api'
import { Classes } from '@blueprintjs/core'
import classnames from 'classnames'

type Sorts = 'workstations' | 'areaSqft' | 'spaceUsageType'

export default function SpacesByUsageTypes() {
  const { object, units, loading } = useContext(TabContext)
  let spaceUsageTypes = object?.spaceUsageTypes

  const scale = useMemo(() => Charts.colorScale(), [])
  const [sort, setSort] = useState<Sorts>('workstations')
  const [sortAscending, setSortAscending] = useState(false)

  const countData =
    spaceUsageTypes?.map((s) => {
      return {
        label: s.readableType,
        value: s.workstations,
        color: `#${scale(s.spaceUsageType)}`,
      }
    }) || []
  const allocatedAreaData =
    spaceUsageTypes?.map((s) => {
      return {
        label: s.readableType,
        value: units === 'sqft' ? s.areaSqft : s.areaSqrm,
        color: `#${scale(s.spaceUsageType)}`,
      }
    }) || []

  spaceUsageTypes = orderBy(spaceUsageTypes, sort, sortAscending ? 'asc' : 'desc')

  const columns: IGridTableColumn<IPortfolioSpaceUsageTypes & { id: string }>[] = [
    {
      label: 'Space Usage Type',
      sort: 'spaceUsageType',
      Cell: ({ cell, skeleton }) => {
        return (
          <div
            data-test="space-usage-type"
            className={classnames({ [Classes.SKELETON]: skeleton })}
          >
            {cell ? (
              <div className="f f-align-center nowrap">
                {cell.data.id !== 'total' && (
                  <ColorMarker color={`#${scale(cell.data.spaceUsageType)}`} />
                )}
                {cell.data.readableType}
              </div>
            ) : (
              'placeholder'
            )}
          </div>
        )
      },
    },
    {
      sort: 'workstations',
      label: 'Spaces',
      Cell: ({ cell, skeleton }) => {
        return (
          <div data-test="spaces-usage" className={classnames({ [Classes.SKELETON]: skeleton })}>
            {cell ? <PrettyNumber value={cell.data.workstations} numeralFormat="0,0" /> : 'place'}
          </div>
        )
      },
    },
    {
      sort: 'areaSqft',
      label: `Allocated Area (${units === 'sqft' ? 'Sqft' : 'Sqrm'})`,
      Cell: ({ cell, skeleton }) => {
        return (
          <div data-test="usage-area" className={classnames({ [Classes.SKELETON]: skeleton })}>
            {cell ? (
              <PrettyNumber
                value={units === 'sqft' ? cell.data.areaSqft : cell.data.areaSqrm}
                numeralFormat="0,0a"
              />
            ) : (
              'place'
            )}
          </div>
        )
      },
    },
  ]

  spaceUsageTypes = orderBy(spaceUsageTypes, sort, sortAscending ? 'asc' : 'desc')

  const formatSpaceUsageTypes = (items: IPortfolioSpaceUsageTypes[]) => {
    return items.map((s) => ({
      data: { id: s.spaceUsageType, ...s },
    }))
  }

  let formatedSpaceTypes = formatSpaceUsageTypes(
    orderBy(spaceUsageTypes, sort, sortAscending ? 'asc' : 'desc')
  )

  //todo use table total line feature (appendData, appendColumn)
  formatedSpaceTypes = [
    ...formatedSpaceTypes,
    ...(object
      ? [
          {
            data: {
              allocated: 0,
              floorArea: 0,
              occupancy: 0,
              spaceUsageType: '',
              unoccupancy: 0,
              unoccupied: 0,
              id: 'total',
              areaSqft: spaceUsageTypes.reduce((acc, s) => acc + s.areaSqft, 0),
              areaSqrm: spaceUsageTypes.reduce((acc, s) => acc + s.areaSqrm, 0),
              occupied: 0,
              readableType: 'Total',
              workstations: spaceUsageTypes.reduce((acc, s) => acc + s.workstations, 0),
            },
          },
        ]
      : []),
  ]

  return (
    <>
      <PrintTitle>
        <h3>Spaces By Usage Types</h3>
      </PrintTitle>
      <table className="basic-table">
        <tbody>
          <tr>
            <td />
            <td>
              <Charts.Donut title="Count" data={countData} height={270} width={370} />
            </td>
            <td>
              <Charts.Donut
                title="Allocated Area"
                data={allocatedAreaData}
                height={270}
                width={370}
              />
            </td>
          </tr>
        </tbody>
      </table>
      <NewGridTable<IPortfolioSpaceUsageTypes & { id: string }>
        status={loading ? GridTableStatusEnum.LOADING : GridTableStatusEnum.IDLE}
        columns={columns}
        data={formatedSpaceTypes}
        sort={{
          column: sort,
          order: sortAscending ? Order.ASC : Order.DESC,
          onSort: (column: string) => {
            if (sort === column) {
              return setSortAscending(!sortAscending)
            } else {
              setSort(column as Sorts)
              setSortAscending(false)
            }
          },
        }}
      />
    </>
  )
}
